import React, { useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

import AdminAuthGuard from '../../components/admin/AdminAuthGuard';
import AdminSidebar from '../../components/admin/AdminSidebar';
import {
  LazyAdminOverview,
  LazyUserManagement,
  LazyWebIDManager,
  LazyAdminAnalytics,
  LazyAdminSettings,
  LazyCouponManager,
  LazyPackManager,
  LazyShippingManager,
  LazyPackageGenerator,
  LazyTrackingDashboard,
  LazyAuditLogs,
  usePreloadAdminComponents,
  AdminPerformanceStats
} from '../../components/admin/LazyAdminComponents';
import { useAuth } from '../../context/AuthContext';

/**
 * Dashboard principal de administración
 * Solo accesible para usuarios con role='admin'
 */
const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Precargar componentes para mejor UX
  usePreloadAdminComponents();

  return (
    <AdminAuthGuard>
    <div className="min-h-screen bg-gray-50">
      {/* Header administrativo */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              {/* Botón hamburger para móvil */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 -ml-2 mr-3 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                aria-label="Abrir menú de navegación"
              >
                {sidebarOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>

              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                <span className="hidden sm:inline">Panel de Administración NFCores</span>
                <span className="sm:hidden">Admin NFCores</span>
              </h1>
              <span className="ml-2 sm:ml-3 inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                Admin
              </span>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-4">
              <span className="hidden md:block text-sm text-gray-600">
                Bienvenido, {user?.displayName || user?.email || 'Administrador'}
              </span>
              <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center">
                <span className="text-white text-sm font-medium">
                  {(user?.displayName || user?.email || 'A')[0].toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar de navegación administrativa */}
        <AdminSidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        {/* Overlay para móvil */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Contenido principal */}
        <main className="flex-1 min-w-0">
          <div className="px-4 sm:px-6 lg:px-8 py-4 lg:py-6">
          <Routes>
            {/* Dashboard principal */}
            <Route index element={<LazyAdminOverview />} />

            {/* Gestión de usuarios */}
            <Route path="users" element={<LazyUserManagement />} />

            <Route path="webids" element={<LazyWebIDManager />} />

            <Route path="analytics" element={<LazyAdminAnalytics />} />

            <Route path="settings" element={<LazyAdminSettings />} />

            <Route path="coupons" element={<LazyCouponManager />} />

            <Route
              path="packs"
              element={
                <React.Suspense
                  fallback={
                    <div className="flex justify-center items-center h-64">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                        <p>Cargando gestión de packs...</p>
                      </div>
                    </div>
                  }
                >
                  <LazyPackManager />
                </React.Suspense>
              }
            />
            {/* Redirección de la ruta antigua para compatibilidad */}
            <Route path="pricing" element={<Navigate to="/admin/packs" replace />} />

            {/* Sistema de Envíos */}
            <Route
              path="shipping"
              element={
                <React.Suspense
                  fallback={
                    <div className="flex justify-center items-center h-64">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                        <p>Cargando gestión de envíos...</p>
                      </div>
                    </div>
                  }
                >
                  <LazyShippingManager />
                </React.Suspense>
              }
            />

            <Route
              path="packaging"
              element={
                <React.Suspense
                  fallback={
                    <div className="flex justify-center items-center h-64">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                        <p>Cargando generador de paquetes...</p>
                      </div>
                    </div>
                  }
                >
                  <LazyPackageGenerator />
                </React.Suspense>
              }
            />

            <Route
              path="tracking"
              element={
                <React.Suspense
                  fallback={
                    <div className="flex justify-center items-center h-64">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                        <p>Cargando dashboard de seguimiento...</p>
                      </div>
                    </div>
                  }
                >
                  <LazyTrackingDashboard />
                </React.Suspense>
              }
            />

            <Route path="audit" element={<LazyAuditLogs />} />

            {/* Redirección por defecto */}
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
          </div>
        </main>
      </div>
    </div>

    {/* Estadísticas de rendimiento (solo en desarrollo) */}
    <AdminPerformanceStats />
    </AdminAuthGuard>
  );
};

export default AdminDashboard;
